import { connect } from "react-redux";
import { SimpleQuestionnaire, useProgress } from "@smartintegrity/questionnaire";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import Accordion from "react-bootstrap/Accordion";
import { withNamespaces } from "react-i18next";
import { Col, Progress, Row, Spinner } from "reactstrap";
import { API_BASE_URL } from "src/modules/data-protection/constants";
import { toast } from "react-toastify";
import { mapAnswersToSave } from "src/modules/data-protection/helpers/questionnaire";

const QuestionsLoadingPlaceholder = () => {
    return (
        <div>
            <Row className="mb-2">
                <Col lg="12">
                    <p style={{width: '100%', height: '55px'}} className={`dt-field dt-skeleton`}></p>
                </Col>
            </Row>
        </div>
    );
}

const ImpactAssessmentQuestionnaire = ({
    questionsQuery,
    assetId,
    t,
    token,
    showImpactAssessment,
    onValidSubmit
}) => {
    const [ questionnaireCompleted, setQuestionnaireCompleted ] = useState(false);

    const {
        control: controlQuestionnaireProgress,
        register: registerQuestionnaireForProgress
    } = useProgress();

    const updateQuestionnaireMutation = useMutation({
        mutationFn: async (payload) => {
            const response = await axios.post(
                `${API_BASE_URL}/dpia/create`,
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    }
                }
            )

            return response.data;
        },
        onSuccess({
            data
        }) {
            toast(t("Report Submitted"), {
                type: 'success'
            });

            onValidSubmit && onValidSubmit(data);
        },
        onError(error) {
            if (process.env.NODE_ENV === 'development') console.error(error)
            toast(t('Error!'), {
                type: "error"
            })
        }
    });

    const questionnaireStatusChangeCallback = useCallback((_currentPayload, newStatus) => {
        setQuestionnaireCompleted(newStatus)
    }, []);

    const handleSubmitQuestionnaire = useCallback((payload) => {
        updateQuestionnaireMutation.mutate({
            questionAnswers: mapAnswersToSave(
                questionsQuery.data?.rawQuestions,
                payload
            ),
            otherAsset: assetId,
            questionnaireType: 'other_asset_data_protection_impact_assessment',
            hardware: null
        })
    }, [
        questionsQuery.data?.rawQuestions,
        updateQuestionnaireMutation,
        assetId
    ]);

    useEffect(() => {
        registerQuestionnaireForProgress('impact-assessment-questionnaire');
    }, [ ]);

    const settings = useMemo(() => {
        let submitButtonSettings = {
            enable: showImpactAssessment && !updateQuestionnaireMutation.isLoading,
            title: updateQuestionnaireMutation.isLoading ? (
                <>
                    <Spinner 
                        size='sm' 
                        color="white"
                        className="me-1"
                    />
                    {`${t('Saving')}...`}
                </>
            ) : t('Save Changes')
        }

        return {
            panelSettings: {
                showNumber: true,
                showProgressBar: false,
                prefix: ''
            },
            buttonsSettings: {
                submit: {
                    display: true,
                    ...submitButtonSettings
                },
                back: {
                    display: false
                }
            },
            fileUploaderOptions: {
                deleteRequestOptions: {
                    headers: {
                        Authorization: `Bearer ${token}`
                    },
                    payload: {},
                    requestUrl: ({
                        questionId,
                        file
                    }) => {
                        return `${API_BASE_URL}/attachment/${file.id}/delete`
                    }
                },
                uploadRequestOptions: {
                    headers: {
                        Authorization: `Bearer ${token}`
                    },
                    payload: {},
                    requestUrl: ({
                        questionId
                    }) => {
                        return `${API_BASE_URL}/report.uploadAttachment`
                    }
                }
            },
            questions: {
                showNumber: true
            }
        }
    }, [
        token,
        t,
        showImpactAssessment,
        updateQuestionnaireMutation.isLoading
    ]);

    if(questionsQuery.isFetching) {
        return (
            <QuestionsLoadingPlaceholder />
        )
    }

    const questionnaireTabsProgressState = controlQuestionnaireProgress.progressQuestionnaires.find(q => q.id === 'impact-assessment-questionnaire');

    const requiredQuestions = questionnaireTabsProgressState?.tabs.reduce((acc, tab) => {
        return acc + (tab.required || 0);
    }, 0) || 0;
    const requiredAnsweredQuestionsCount = questionnaireTabsProgressState?.tabs.reduce((acc, tab) => {
        return acc + (tab.requiredAnswered || 0);
    }, 0) || 0;

    const optionalQuestions = questionnaireTabsProgressState?.tabs.reduce((acc, tab) => {
        return acc + (tab.optional || 0);
    }, 0) || 0;
    const optionalAnsweredQuestionsCount = questionnaireTabsProgressState?.tabs.reduce((acc, tab) => {
        return acc + (tab.optionalAnswered || 0);
    }, 0) || 0;


    let tabRequiredPercent = 0
    let tabOptionalPercent = 0

    if (requiredQuestions) {
        tabRequiredPercent = (requiredAnsweredQuestionsCount / requiredQuestions * 100).toFixed(0)
    }

    if (optionalQuestions) {
        tabOptionalPercent = (optionalAnsweredQuestionsCount / optionalQuestions * 100).toFixed(0)
    }

    return (
        <Accordion.Item
            className="border-none mb-2"
            eventKey="impact-assessment-questionnaire"
            disabled={!showImpactAssessment}
            style={{
                opacity: !showImpactAssessment ? "0.5" : "1",
                cursor: !showImpactAssessment ? "not-allowed" : "pointer"
            }}
        >
            <div className="d-flex justify-content-between align-items-center p-3 bg-white">
                <Accordion.Button 
                    className="bg-white"
                >
                    <div className="d-flex align-items-center justify-content-between w-100">
                        <div title={t("Available for high risk assets")}>
                            {t("Data Protection Impact Assessment")}
                        </div>

                        <div className="completed-percent">
                            <div className={`required-questions ${!requiredQuestions ? 'd-none' : ''}`}>
                                <div className='titles'>
                                    <span className='group-title'>
                                        {`${t('Mandatory')}: ${requiredAnsweredQuestionsCount}/${requiredQuestions}`}
                                    </span>
                                    <span className='percent-title'>
                                        {tabRequiredPercent >= 100 ? t('Completed') : `${tabRequiredPercent}%`}
                                    </span>
                                </div>
                                <Progress 
                                    className={tabRequiredPercent >= 100 ? 'completed' : 'incompleted'} 
                                    color="success" 
                                    value={tabRequiredPercent} 
                                />
                            </div>

                            <div className={`optional-questions ${!optionalQuestions ? 'd-none' : ''}`}>
                                <div className='titles'>
                                    <span className='group-title'>
                                        {`${t('Optional')}: ${optionalAnsweredQuestionsCount}/${optionalQuestions}`}
                                    </span>
                                    <span className='percent-title'>
                                        {tabOptionalPercent >= 100 ? t('Completed') : `${tabOptionalPercent}%`}
                                    </span>
                                </div>
                                <Progress 
                                    className={tabOptionalPercent >= 100 ? 'completed' : 'incompleted'} 
                                    color="success" 
                                    value={tabOptionalPercent} 
                                />
                            </div>
                        </div>
                    </div>
                </Accordion.Button>
            </div>


            <Accordion.Body>
                <SimpleQuestionnaire 
                    questionnaireUniqueId='impact-assessment-questionnaire'
                    className='gdpr'
                    questions={questionsQuery.data?.mappedQuestions || []}
                    defaultValues={questionsQuery.data?.mappedAnswers || []}
                    settings={settings}
                    onQuestionnaireCompleteStatusChanged={questionnaireStatusChangeCallback}
                    onSubmit={handleSubmitQuestionnaire}
                    {...controlQuestionnaireProgress}
                />
            </Accordion.Body>
        </Accordion.Item>
    )
}

export default withNamespaces()(
    connect((state) => {
        const { token, user } = state.Login;

        return {
            token,
            user
        };
    })(
        ImpactAssessmentQuestionnaire
    )
);